import { type HTMLAttributes } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';

/* ─── Card ─────────────────────────────────────────────────── */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Remove border-radius and shadow for flush/inset cards */
  flush?: boolean;
  /** Add interactive hover lifting scale and transition */
  interactive?: boolean;
}

/**
 * Card — Primary surface container.
 * Maps to the `.card` class in index.css (background: var(--surface), border, border-radius 14px).
 *
 * NO business logic — pure presentational container.
 */
export function Card({ className, flush, interactive = false, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'bg-[var(--surface)] border border-[var(--border)] transition-[border-color,box-shadow,transform] duration-200 ease-out',
        !flush && 'rounded-[14px]',
        interactive && 'hover:-translate-y-[2px] hover:shadow-[var(--shadow-2)] hover:border-[var(--border-strong)]',
        className
      )}
      {...props}
    />
  );
}

/* ─── CardHeader ───────────────────────────────────────────── */
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Render a bottom border separator */
  bordered?: boolean;
}

export function CardHeader({ className, bordered = false, ...props }: CardHeaderProps) {
  return (
    <div
      className={cn(
        'px-[18px] pt-[16px] pb-[4px]',
        bordered && 'border-b border-[var(--border)] pb-[12px]',
        className
      )}
      {...props}
    />
  );
}

/* ─── CardTitle ────────────────────────────────────────────── */
export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn('text-[14.5px] font-medium text-[var(--text)]', className)}
      {...props}
    />
  );
}

/* ─── CardContent ──────────────────────────────────────────── */
export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-[18px] py-[12px]', className)} {...props} />
  );
}

/* ─── CardFooter ───────────────────────────────────────────── */
export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'px-[18px] pt-[12px] pb-[16px] border-t border-[var(--border)] flex items-center gap-3',
        className
      )}
      {...props}
    />
  );
}
